import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'

import CancelButton from '../../components/psc/lib/CancelButton'

import useAuthentication from '../../hooks/useAuthentication';
import useUserManagement from '../../hooks/useUserManagement';

const Account2FA = () => {
    const navigate = useNavigate();

    const {
        currentUser,
        updateUser,
    } = useAuthentication();
    
    const {
        updateUserAndSave,
    } = useUserManagement()
    
    const [status, setStatus] = useState(currentUser.is_2fa_configured ? 'Disable' : 'Disabled');

    const [qrCode, setQrCode] = useState('');
    const is2FARequired = currentUser.is_2fa_required

    const [verificationCode, setVerificationCode] = useState('');
    const [verificationCodeDisable, setVerificationCodeDisable] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetch('/v0/users/' + currentUser.id + '/qr_code', { credentials: 'same-origin' })
        .then(function(response) {
            return response.json()
        })
        .then(function(json) {
            setQrCode(json.result.Code)
        })
    }, [])

    const handleContinue = (event) => {
        if(event) {
            event.preventDefault()
        }

        if( is2FARequired ) {
            // In case the user was forced to set up, push them to /main
            // Otherwise they're most likely to be sent to /login,
            // which would force them to login again
            navigate('/main')
            return
        }

        navigate('/login')
    }

    const handleEnableNow = (event) => {
        if(event) {
            event.preventDefault()
        }
        setStatus('Enable')
    }

    const handleEnable2FA = (event) => {
        event.preventDefault()

        let errors = []
        if ( ! verificationCode ) {
            errors.push('Verification Code is required')
        }
        if ( errors.length > 0 ) {
            setError(errors)
            return
        }

        const data = { verification_code: verificationCode }
        fetch('/v0/account/enable_2fa', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(response => {
            if (!response.ok) throw response
            return response.json()
        })
        .then(json => {
            updateUser(json.result);
            updateUserAndSave(currentUser.id, { is_2fa_configured: true })
            setStatus('Enabled')
        })
        .catch(err => {
            let msg = err.json().error
            if( msg === 'EBADTOKEN' ) {
                msg = 'Invalid Verification Code';
            }
            setError(msg)
        })
    }

    const handleDisable2FA = (event) => {
        event.preventDefault()

        let errors = []

        if ( is2FARequired ) {
            errors.push('Two Factor Authentication is required for this account')
        }

        if ( ! verificationCodeDisable ) {
            errors.push('Verification Code is required')
        }
        if ( errors.length > 0 ) {
            setError(errors)
            return
        }

        const data = { verification_code: verificationCodeDisable }
        fetch('/v0/account/disable_2fa', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        })
        .then(response => {
            if (!response.ok) throw response
            return response.json()
        })
        .then(json => {
            updateUser(json.result);
            updateUserAndSave(currentUser.id, { is_2fa_configured: false })
            setStatus('NowDisabled')
        })
        .catch(err => {
            let msg = err.json().error
            if( msg === 'EBADTOKEN' ) {
                msg = 'Invalid Verification Code';
            }
            setError(msg)
        })
    }

    // All the rendering
    const renderStatusDisabled = () => {
        let text = (
            <div>
                <h3>Two Factor Authentication is currently Disabled.</h3>

                <p>
                    If you enable Two Factor Authentication (2FA), when you use your
                    password to login to GNC Go you will need to generate an additional
                    verification code using an authenticator app such as Google Authenticator or Microsoft Authenticator.
                </p>
            </div>
        )

        if(is2FARequired) {
            text = (
                <div>
                    <h3>Let's get you set up...</h3>

                    <p>
                        Two Factor Authentication (2FA) has been enabled for your account, or has been reset.
                    </p>

                    <p>
                        With 2FA, when you use your password to login to GNC Go you will need to generate
                        an additional verification code using an authenticator app such as
                        Google Authenticator or Microsoft Authenticator.
                    </p>
                </div>
            )
        }

        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>
                                    Two Factor Authentication
                                </h2>
                            </div>
                            <div className="ibox-content">
                                <form className="form-horizontal" autoComplete="off" onSubmit={handleEnableNow}>

                                    {text}

                                    <div className="hr-line-dashed"></div>

                                    <div className="form-group">
                                        <div className="col-sm-8 col-sm-offset-2">
                                            <button className="btn btn-mr btn-primary" type="submit">Continue</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderStatusEnable = () => {
        const qrImage = '/v0/users/' + currentUser.id + '/qr'
        
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>
                                    Authenticator App Configuration
                                </h2>
                            </div>
                            <div className="ibox-content">
                                <form className="form-horizontal" autoComplete="off" onSubmit={handleEnable2FA}>

                                    <div className="form-group">
                                        <label className="col-sm-2 control-label"> </label>
                                        <div className="col-sm-8">
                                            <p>
                                                Scan this code with your authenticator app:
                                            </p>

                                            <img src={qrImage} width="250" height="250" />
                                            
                                            <p>
                                                Optionally use this setup key: <strong>{qrCode}</strong>
                                            </p>
                                            
                                            <p>
                                                Once you’ve added GNC Go to your authenticator app, enter the 6-digit code below.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">Verification Code</label>
                                        <div className="col-sm-8">
                                            <input className="form-control"
                                                type="text"
                                                value={verificationCode}
                                                onChange={e => setVerificationCode(e.target.value)}
                                                required />
                                        </div>
                                    </div>

                                    <div className="hr-line-dashed"></div>

                                    <div className="form-group">
                                        {error && <div className="alert alert-danger">{error}</div>}

                                        <div className="col-sm-8 col-sm-offset-2">
                                            <CancelButton />

                                            <button className="btn btn-mr btn-primary" type="submit">Enable Two Factor Authentication</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderStatusEnabled = () => {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>
                                    Two Factor Authentication
                                </h2>
                            </div>
                            <div className="ibox-content">
                                <form className="form-horizontal" autoComplete="off" onSubmit={handleContinue}>

                                    <h3>Two Factor Authentication is now Enabled.</h3>

                                    <p>
                                        When you use your password to login to GNC Go you will need to generate an
                                        additional verification code using your authenticator app.
                                    </p>

                                    <div className="hr-line-dashed"></div>

                                    <div className="form-group">
                                        <div className="col-sm-8 col-sm-offset-2">
                                            <button className="btn btn-mr btn-primary" type="submit">Continue</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderStatusDisable = () => {
        if ( is2FARequired ) {
            return renderStatusEnabled()
        }

        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>
                                    Disable Two Factor Authentication
                                </h2>
                            </div>
                            <div className="ibox-content">
                                <form className="form-horizontal" autoComplete="off" onSubmit={handleDisable2FA}>

                                    <h3>Two Factor Authentication is currently Enabled.</h3>

                                    <p>
                                        If you disable Two Factor Authentication (2FA) your account will be less secure.
                                        You can re-enable this at any time.
                                    </p>

                                    <p>
                                        To continue, enter a 6-digit verification code below.
                                    </p>

                                    <div className="form-group">
                                        <label className="col-sm-2 control-label">Verification Code</label>
                                        <div className="col-sm-8">
                                            <input className="form-control"
                                                type="text"
                                                value={verificationCodeDisable}
                                                onChange={e => setVerificationCodeDisable(e.target.value)}
                                                required />
                                        </div>
                                    </div>

                                    <div className="hr-line-dashed"></div>

                                    <div className="form-group">
                                        {error && <div className="alert alert-danger">{error}</div>}

                                        <div className="col-sm-8 col-sm-offset-2">
                                            <CancelButton />

                                            <button className="btn btn-mr btn-primary" type="submit">Disable Two Factor Authentication</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const renderStatusNowDisabled = () => {
        return (
            <div className="wrapper wrapper-content">
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-title">
                                <h2>
                                    Two Factor Authentication
                                </h2>
                            </div>
                            <div className="ibox-content">
                                <form className="form-horizontal" autoComplete="off" onSubmit={handleContinue}>

                                    <h3>Two Factor Authentication is now Disabled.</h3>

                                    <p>
                                        Your account is no longer protected by Two Factor Authentication.
                                    </p>

                                    <div className="hr-line-dashed"></div>

                                    <div className="form-group">
                                        <div className="col-sm-8 col-sm-offset-2">
                                            <button className="btn btn-mr btn-primary" type="submit">Continue</button>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    if( status === 'Disabled' ) {
        return renderStatusDisabled()
    } else if ( status === 'Disable' ) {
        return renderStatusDisable()
    } else if( status === 'Enable' ) {
        return renderStatusEnable()
    } else if( status === 'Enabled' ) {
        return renderStatusEnabled()
    } else if( status === 'NowDisabled' ) {
        return renderStatusNowDisabled()
    }
};

export default Account2FA
